// Curves gallery route: typed search params (document, search text, topics) and the gallery; an old ?curve= link is redirected to the chart that draws that curve, or loses the parameter when the curve is unknown.
import { createFileRoute, isRedirect, redirect } from "@tanstack/react-router";
import { useCallback } from "react";

import { curvesGetCurveOptions } from "@/api/__generated__/@tanstack/react-query.gen";
import { chartKeyOf, docSearch } from "@/features/curves/charts";
import { ChartsScreen } from "@/features/curves/ChartsScreen";
import { curvesSearchSchema, type CurvesSearchPatch } from "@/features/curves/search";
import { docUidOf } from "@/lib/url";

export const Route = createFileRoute("/_app/$region/curves")({
  validateSearch: curvesSearchSchema,
  beforeLoad: async ({ context, params, search }) => {
    if (search.curve === undefined) {
      return;
    }
    const { curve: curveUid, ...rest } = search;
    try {
      const curve = await context.queryClient.query({
        ...curvesGetCurveOptions({ path: { curve_uid: curveUid } }),
        staleTime: "static",
      });
      const chartKey = chartKeyOf(curve);
      if (chartKey !== null) {
        throw redirect({
          to: "/$region/curves/$chartKey",
          params: { region: params.region, chartKey },
          search: docSearch(docUidOf(curveUid), context.region.currentDocUid),
          replace: true,
        });
      }
    } catch (error) {
      if (isRedirect(error)) {
        throw error;
      }
    }
    throw redirect({ to: "/$region/curves", params, search: rest, replace: true });
  },
  staticData: { crumb: "Curves" },
  component: CurvesRoute,
});

function CurvesRoute() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const onSearchChange = useCallback(
    (patch: CurvesSearchPatch, replace = false) => {
      void navigate({ search: (previous) => ({ ...previous, ...patch }), replace });
    },
    [navigate],
  );
  return <ChartsScreen search={search} onSearchChange={onSearchChange} />;
}
